import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card } from '@/components/ui/card';
import { ShieldCheck, BookOpen, Code, Puzzle, ShipWheel } from 'lucide-react';
import { formatIntegrationName } from '../../../utils/formatInternalName';
import { useTheme } from '@/components/theme-provider';
import slackColorIcon from '../../../assets/icons/slack-color.png';
import pagerdutyColorIcon from '../../../assets/icons/pagerduty-icon.svg';
import confluenceIcon from '../../../assets/icons/confluence.svg';
import jiraIcon from '../../../assets/icons/jira.svg';
import linearIcon from '../../../assets/icons/linear.png';
import linearDarkIcon from '../../../assets/icons/linear-dark-mode.png';
import datadogLightIcon from '../../../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../../../assets/icons/datadog-dark-mode.png';
import gitlabIcon from '../../../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../../../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../../../assets/icons/kestrel-dark.svg';
import awsLightLogo from '../../../assets/icons/aws.svg';
import awsDarkLogo from '../../../assets/icons/aws-dark.svg';
import argocdIcon from '../../../assets/icons/argocd.svg';
import fluxcdIcon from '../../../assets/icons/flux-cd.svg';
import karpenterIcon from '../../../assets/icons/karpenter.svg';
import kyvernoIcon from '../../../assets/icons/kyverno.svg';
import trivyIcon from '../../../assets/icons/trivy.svg';
import helmLightIcon from '../../../assets/icons/helm-light.svg';
import helmDarkIcon from '../../../assets/icons/helm-dark.svg';
import posthogIcon from '../../../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../../../assets/icons/posthog-light.png';
import vercelIcon from '../../../assets/icons/vercel.svg';
import vercelLightIcon from '../../../assets/icons/vercel-light.svg';
import railwayIcon from '../../../assets/icons/railway.svg';
import flyioIcon from '../../../assets/icons/flyio.svg';
import nebiusIcon from '../../../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../../../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../../../assets/icons/daytona.svg';
import supabaseIcon from '../../../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../../../assets/icons/planetscale-mono.svg';
import neonIcon from '../../../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../../../assets/icons/clickhouse.svg';
import jenkinsIcon from '../../../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../../../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../../../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../../../assets/icons/okta-mono.svg';
import databricksIcon from '../../../assets/icons/databricks.png';
import { ReactComponent as GCPActionIcon } from '../../../assets/icons/gcp.svg';
import terraformIcon from '../../../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../../../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../../../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../../../assets/icons/infisical.svg';
import cloudflareIcon from '../../../assets/icons/cloudflare.svg';

const DEFAULT_COLORS = {
  bg: 'bg-zinc-50 dark:bg-zinc-900/40',
  border: 'border-zinc-300 dark:border-zinc-700',
  text: 'text-zinc-500 dark:text-zinc-400',
  accent: 'bg-zinc-100 dark:bg-zinc-800',
};

// sizeOnly drops text-colour utilities from a className, keeping sizing. Brand
// icons that must render in their own colour need this because the caller passes
// a muted text colour for the monochrome fallback icons.
function sizeOnly(cls: string): string {
  return cls
    .split(' ')
    .filter(c => !/^(text-|dark:text-)/.test(c))
    .join(' ');
}

function IntegrationIcon({ integration, className, data }: { integration: string; className?: string; data?: any }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const cls = className || 'w-4 h-4';

  if (integration === 'custom' && data) {
    const lightIcon = data.custom_icon_url || '';
    const darkIcon = data.custom_icon_url_dark || '';
    const resolvedIcon = isDark ? (darkIcon || lightIcon) : (lightIcon || darkIcon);
    if (resolvedIcon) {
      return <img src={resolvedIcon} alt="Custom" className={`${cls} object-contain`} />;
    }
    return <Puzzle className={cls} />;
  }

  switch (integration) {
    case 'kestrel':
      return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" className={cls} />;
    case 'github':
      return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className={cls} />;
    case 'gitlab':
      return <img src={gitlabIcon} alt="GitLab" className={cls} />;
    case 'slack':
      return <img src={slackColorIcon} alt="Slack" className={cls} />;
    case 'pagerduty':
      return <img src={pagerdutyColorIcon} alt="PagerDuty" className={cls} />;
    case 'confluence':
      return <img src={confluenceIcon} alt="Confluence" className={cls} />;
    case 'jira':
      return <img src={jiraIcon} alt="Jira" className={cls} />;
    case 'linear':
      return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" className={cls} />;
    case 'datadog':
      return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className={cls} />;
    case 'aws':
    case 'aws-cost':
    case 'aws-ops':
    case 'aws-security':
    case 'aws-data':
      return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className={cls} />;
    case 'kubernetes':
      // Strip the caller's text colour before applying blue. The call site passes
      // colors.text (text-zinc-500 dark:text-zinc-400) for the monochrome lucide
      // fallbacks, and appending text-blue-500 to that leaves two competing text
      // utilities, so the icon rendered grey.
      return <ShipWheel className={`${sizeOnly(cls)} text-blue-500`} />;
    case 'argocd':
      return <img src={argocdIcon} alt="ArgoCD" className={cls} />;
    case 'argo-rollouts':
      return <img src={argocdIcon} alt="Argo Rollouts" className={cls} />;
    case 'fluxcd':
      return <img src={fluxcdIcon} alt="Flux CD" className={cls} />;
    case 'karpenter':
      return <img src={karpenterIcon} alt="Karpenter" className={cls} />;
    case 'kyverno':
      return <img src={kyvernoIcon} alt="Kyverno" className={cls} />;
    case 'trivy':
      return <img src={trivyIcon} alt="Trivy" className={cls} />;
    case 'helm':
      return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" className={cls} />;
    case 'posthog':
      return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className={`${cls} object-contain`} />;
    case 'vercel':
      return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className={cls} />;
    case 'railway':
      return <img src={railwayIcon} alt="Railway" className={`${cls} object-contain`} style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
    case 'flyio':
      return <img src={flyioIcon} alt="Fly.io" className={`${cls} object-contain`} />;
    case 'nebius':
      return <img src={nebiusIcon} alt="Nebius" className={`${cls} object-contain`} />;
    case 'beam':
      // Strip any inherited text-color (e.g. the node's gray colors.text, incl.
      // variant prefixes like dark:) so the brand icon stays theme-adaptive:
      // white in dark mode, black in light mode.
      return <BeamIcon className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} text-foreground`} />;
    case 'daytona':
      return <DaytonaIcon className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} text-foreground`} />;
    case 'supabase':
      return <img src={supabaseIcon} alt="Supabase" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'planetscale':
      return <PlanetScaleIcon className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} text-foreground`} />;
    case 'neon':
      return <img src={neonIcon} alt="Neon" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'clickhouse':
      return <img src={clickhouseIcon} alt="ClickHouse" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'jenkins':
      return <img src={jenkinsIcon} alt="Jenkins" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'circleci':
      return <CircleCIIcon className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} text-foreground`} />;
    case 'sonarcloud':
      return <SonarCloudIcon className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} text-foreground`} />;
    case 'okta':
      return <OktaIcon className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} text-foreground`} />;
    case 'databricks':
      return <img src={databricksIcon} alt="Databricks" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'gcp-cost':
    case 'gcp':
    case 'gcp-security':
    case 'gcp-data':
      return <GCPActionIcon className={`${sizeOnly(cls)} object-contain`} />;
    case 'terraform':
      return <img src={terraformIcon} alt="Terraform" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'pulumi':
      return <img src={pulumiIcon} alt="Pulumi" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'vault':
      return <img src={vaultIcon} alt="Vault" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain`} />;
    case 'infisical':
      return <img src={infisicalIcon} alt="Infisical" className={`${cls.replace(/(?:[a-z-]+:)?text-\S+/g, '').replace(/\s+/g, ' ').trim()} object-contain rounded-sm`} />;
    case 'cloudflare':
      return <img src={cloudflareIcon} alt="Cloudflare" className={cls} />;
    case 'approval':
      return <ShieldCheck className={cls} />;
    case 'notion':
      return <BookOpen className={cls} />;
    default:
      return <Code className={cls} />;
  }
}

export function ActionNode({ data, selected }: any) {
  const integration = data.integration || 'kestrel';
  const customColor = data.custom_color || '';
  const colors = integration === 'custom' && customColor
    ? { bg: 'bg-background', border: 'border-border', text: 'text-foreground', accent: 'bg-muted' }
    : DEFAULT_COLORS;

  const integrationLabel = integration === 'custom'
    ? (data.integration_name || 'CUSTOM')
    : formatIntegrationName(integration);

  return (
    <div className="relative">
      <Handle type="target" position={Position.Left} className="!bg-primary !w-2 !h-2" />
      <Handle type="source" position={Position.Right} className="!bg-primary !w-2 !h-2" />
      <Card
        className={`w-[260px] p-3 border-2 transition-shadow cursor-pointer ${
          selected ? 'shadow-lg ring-2 ring-primary/30 border-primary' : colors.border
        } ${colors.bg}`}
        style={integration === 'custom' && customColor ? { borderColor: customColor + '60' } : undefined}
      >
        <div className="flex items-center gap-2 mb-1">
          <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${colors.accent}`}
            style={integration === 'custom' && customColor ? { backgroundColor: customColor + '20' } : undefined}>
            <IntegrationIcon integration={integration} className={`w-4 h-4 ${colors.text}`} data={data} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-medium uppercase tracking-wide ${colors.text}`}
              style={integration === 'custom' && customColor ? { color: customColor } : undefined}>
              {integrationLabel}
            </div>
            <div className="text-sm font-semibold truncate">{data.label || data.action || 'Action'}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
